/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { TooltipHost } from 'office-ui-fabric-react';
import DetailsList from '../DetailsList';
import styles from './DimensionLists.style';
import { formatDateString } from '../../utils';

const DimensionLists = ({
  items,
  reviewerItems,
  className: classNameProp,
  sortColumn,
  sortAscending,
  setSortColumn,
  setSortAscending,
  ...other
}) => {
  const handleColumnClick = (event, column) => {
    setSortAscending(!sortAscending);
    setSortColumn(column.sortColumnName);
  };

  const columns = {
    status: {
      key: 'status',
      name: 'status',
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.actionIndicatorIcon,
      arianame: 'status',
      iconName: 'StatusCircleExclamation',
      isIconOnly: true,
      fieldName: 'status',
      minWidth: 10,
      maxWidth: 10,
      columnActionsMode: 0,
      onRender: (item, index) => {
        if (item?.status === 'Pending' || item?.status === 'Rejected') {
          return (
            <TooltipHost
              content="Action Required"
              aria-label="Action Required"
              calloutProps={{
                gapSpace: 0,
                target: `#${`actionIcon${index}`}`,
              }}
            >
              <div className={styles.actionIndicator}>
                <FontIcon
                  iconName="StatusCircleExclamation"
                  ariaLabel="Status"
                  id={`actionIcon${index}`}
                />
              </div>
            </TooltipHost>
          );
        }
        return '';
      },
    },
    icon: {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      arianame: 'icon',
      iconName: 'BulletedList',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 8,
      maxWidth: 8,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon iconName="BulletedList" arianame="BulletedList" />
          </div>
        );
      },
    },
    name: {
      key: 'listName',
      name: 'List Name',
      fieldName: 'listName',
      sortColumnName: 'listName',
      minWidth: 180,
      maxWidth: 280,
      isMultiline: true,
      isSorted: sortColumn === 'listName',
      isSortedDescending: !sortAscending,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return <div className={styles.listName}>{item.listName}</div>;
      },
    },
    creator: {
      key: 'createdBy',
      name: 'Created By',
      fieldName: 'createdBy',
      sortColumnName: 'metadata.createdBy.displayName',
      minWidth: 150,
      maxWidth: 200,
      isMultiline: true,
      isSorted: sortColumn === 'metadata.createdBy.displayName',
      isSortedDescending: !sortAscending,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return <div className={styles.createdBy}>{item.metadata?.createdBy?.displayName} </div>;
      },
    },
    created: {
      key: 'createdTimeStamp',
      name: 'Date Created',
      fieldName: 'createdTimeStamp',
      sortColumnName: 'metadata.createdTimeStamp',
      minWidth: 100,
      maxWidth: 140,
      isSorted: sortColumn === 'metadata.createdTimeStamp',
      isSortedDescending: !sortAscending,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return <div>{formatDateString(item.metadata?.createdTimeStamp)}</div>;
      },
    },
    modified: {
      key: 'lastUpdatedTimestamp',
      name: 'Last Modified',
      fieldName: 'lastUpdatedTimestamp',
      sortColumnName: 'metadata.lastUpdatedTimestamp',
      minWidth: 100,
      maxWidth: 140,
      isSorted: sortColumn === 'metadata.lastUpdatedTimestamp',
      isSortedDescending: !sortAscending,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return <div>{formatDateString(item.metadata?.lastUpdatedTimestamp)}</div>;
      },
    },
    contracts: {
      key: 'associatedContracts',
      name: '# Assoc. Contracts',
      fieldName: 'associatedContracts',
      sortColumnName: 'associatedContracts',
      minWidth: 100,
      maxWidth: 140,
      isSorted: sortColumn === 'associatedContracts',
      isSortedDescending: !sortAscending,
      onColumnClick: handleColumnClick,
      onRender: item => {
        return <div>{item.associatedContracts ? item.associatedContracts.length : 0}</div>;
      },
    },
  };

  const dimensionListsPageColumns = items?.some(item =>
    ['Pending', 'Rejected'].includes(item?.status),
  )
    ? [
        columns.status,
        columns.icon,
        columns.name,
        columns.creator,
        columns.created,
        columns.modified,
        columns.contracts,
      ]
    : [
        columns.icon,
        columns.name,
        columns.creator,
        columns.created,
        columns.modified,
        columns.contracts,
      ];

  const className = clsx(classNameProp);

  return (
    <DetailsList
      items={items.map(dimensionLists => ({
        ...dimensionLists,
        key: dimensionLists?.listId,
        link:
          reviewerItems?.length &&
          reviewerItems.some(
            task => task?.resourceId === dimensionLists?.listId && task?.operation === 'reject',
          )
            ? `/dimensionLists/${dimensionLists?.listId}/edit`
            : `/dimensionLists/${dimensionLists?.listId}/${dimensionLists?.listType}`,
        isOpenInNewTab: true,
      }))}
      columns={dimensionListsPageColumns}
      className={className}
      {...other}
      data-testid="dimensionListTable"
    />
  );
};

DimensionLists.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      listId: PropTypes.string,
      listType: PropTypes.string,
      listName: PropTypes.string,
      listDescription: PropTypes.string,
      listItems: PropTypes.array,
      associatedLists: PropTypes.array,
      metadata: PropTypes.object,
    }),
  ),
  reviewerItems: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string.isRequired,
      resourceId: PropTypes.string.isRequired,
      operation: PropTypes.string.isRequired,
    }),
  ),
  className: PropTypes.string,
  sortColumn: PropTypes.string,
  sortAscending: PropTypes.bool,
  setSortColumn: PropTypes.func,
  setSortAscending: PropTypes.func,
};

DimensionLists.defaultProps = {
  items: [],
  className: '',
  reviewerItems: [],
  sortColumn: '',
  sortAscending: false,
  setSortColumn: () => {},
  setSortAscending: () => {},
};

export default DimensionLists;
